import { useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles, ArrowLeft, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api from "@/lib/api";
import { toast } from "sonner";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#F5F7FA]">
      <div className="flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <Link to="/" className="flex items-center mb-12">
            <img src="/promptyai-logo.png" alt="Promptyai.app" className="h-16 w-auto object-contain" />
          </Link>

          {!sent ? (
            <>
              <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mb-2">Forgot your password?</h1>
              <p className="text-[#64748B] mb-8">Enter your email and we'll send you a link to reset it.</p>
              <form onSubmit={submit} className="space-y-5">
                <div>
                  <Label htmlFor="email">Email</Label>
                  <Input id="email" data-testid="forgot-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="mt-1.5" />
                </div>
                <Button type="submit" disabled={loading} className="w-full bg-[#1F3A8A] hover:bg-[#1E40AF] text-white" data-testid="forgot-submit">
                  {loading ? "Sending…" : "Send reset link"}
                </Button>
                <Link to="/login" className="flex items-center justify-center gap-2 text-sm text-[#64748B] hover:text-[#1F3A8A] pt-2">
                  <ArrowLeft className="w-3.5 h-3.5" /> Back to sign in
                </Link>
              </form>
            </>
          ) : (
            <div data-testid="forgot-sent">
              <div className="w-14 h-14 rounded-full bg-[#14B8A6]/10 flex items-center justify-center mb-6">
                <Check className="w-7 h-7 text-[#14B8A6]" strokeWidth={2.5} />
              </div>
              <h1 className="font-display text-3xl font-bold tracking-tight mb-3">Check your inbox.</h1>
              <p className="text-[#64748B] mb-8">If an account exists for <strong className="text-[#111827]">{email}</strong>, we've sent a link to reset your password. The link expires in 30 minutes.</p>
              <Link to="/login">
                <Button className="w-full bg-[#1F3A8A] hover:bg-[#1E40AF] text-white">Back to sign in</Button>
              </Link>
            </div>
          )}
        </div>
      </div>
      <div className="hidden lg:block relative bg-[#1F3A8A] bg-grid-dark overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#1F3A8A] via-[#1F3A8A] to-[#14B8A6]/30"></div>
        <div className="relative h-full flex items-end p-12 text-white">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#14B8A6] mb-3">Secure by default</div>
            <p className="font-display text-3xl font-bold leading-tight max-w-md">Reset links are single-use and expire after 30 minutes.</p>
          </div>
        </div>
      </div>
    </div>
  );
}
